import { withFirebaseAuthorization } from './authFetch';

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function normalizeApiError(response, payload) {
  return {
    status: response.status,
    error:
      payload?.error ||
      payload?.message ||
      `Request failed with status ${response.status}.`,
    requestId: payload?.requestId || payload?.data?.requestId,
  };
}

export function createFamilyClient(baseUrl, fetchImpl = fetch) {
  const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
  const userBaseUrl = (userId) =>
    `${normalizedBaseUrl}/api/users/${encodeURIComponent(userId)}`;

  async function request(url, options = {}) {
    const response = await fetchImpl(url, await withFirebaseAuthorization(options));
    const payload = await parseJson(response);

    if (!response.ok) {
      throw normalizeApiError(response, payload);
    }

    return payload;
  }

  return {
    getFamily(userId) {
      return request(`${userBaseUrl(userId)}/family`);
    },

    createFamily(userId, { name }) {
      return request(`${userBaseUrl(userId)}/family`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
        }),
      });
    },

    getFamilyMembers(userId) {
      return request(`${userBaseUrl(userId)}/family/members`);
    },

    getFamilyInvites(userId, { status } = {}) {
      const query = new URLSearchParams();
      if (status) {
        query.set('status', status);
      }

      return request(
        `${userBaseUrl(userId)}/family/invites${query.toString() ? `?${query}` : ''}`
      );
    },

    getIncomingFamilyInvites(userId) {
      return request(`${userBaseUrl(userId)}/family/incoming-invites`);
    },

    getFamilyTasks(userId, filters = {}) {
      const query = new URLSearchParams();
      if (filters?.status) {
        query.set('status', filters.status);
      }
      if (filters?.assignedTo) {
        query.set('assignedTo', filters.assignedTo);
      }

      return request(
        `${userBaseUrl(userId)}/family/tasks${query.toString() ? `?${query}` : ''}`
      );
    },

    createFamilyTasks(userId, { tasks }) {
      return request(`${userBaseUrl(userId)}/family/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tasks,
        }),
      });
    },

    updateFamilyTask(userId, taskId, updates) {
      return request(`${userBaseUrl(userId)}/family/tasks/${encodeURIComponent(taskId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates || {}),
      });
    },

    completeFamilyTask(userId, taskId) {
      return request(
        `${userBaseUrl(userId)}/family/tasks/${encodeURIComponent(taskId)}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );
    },

    getFamilyLists(userId) {
      return request(`${userBaseUrl(userId)}/family/lists`);
    },

    createFamilyList(userId, list) {
      return request(`${userBaseUrl(userId)}/family/lists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(list || {}),
      });
    },

    updateFamilyList(userId, listId, updates) {
      return request(`${userBaseUrl(userId)}/family/lists/${encodeURIComponent(listId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates || {}),
      });
    },

    getFamilyListItems(userId, listId, { status } = {}) {
      const query = new URLSearchParams();
      if (status && status !== 'all') {
        query.set('status', status);
      }

      return request(
        `${userBaseUrl(userId)}/family/lists/${encodeURIComponent(listId)}/items${
          query.toString() ? `?${query}` : ''
        }`
      );
    },

    createFamilyListItems(userId, listId, { items }) {
      return request(`${userBaseUrl(userId)}/family/lists/${encodeURIComponent(listId)}/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items,
        }),
      });
    },

    updateFamilyListItem(userId, listId, itemId, updates) {
      return request(
        `${userBaseUrl(userId)}/family/lists/${encodeURIComponent(
          listId
        )}/items/${encodeURIComponent(itemId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates || {}),
        }
      );
    },

    createFamilyInvite(userId, { email, role }) {
      return request(`${userBaseUrl(userId)}/family/invites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          role,
        }),
      });
    },

    acceptFamilyInvite(userId, { token }) {
      return request(`${userBaseUrl(userId)}/family/invites/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
        }),
      });
    },

    acceptIncomingFamilyInvite(userId, inviteId) {
      return request(
        `${userBaseUrl(userId)}/family/incoming-invites/${encodeURIComponent(inviteId)}/accept`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );
    },

    revokeFamilyInvite(userId, inviteId) {
      return request(
        `${userBaseUrl(userId)}/family/invites/${encodeURIComponent(inviteId)}/revoke`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );
    },
  };
}
